import React, { useState } from "react";
import {
    Box, Container, Paper, Typography, TextField, Button, Stack,
    RadioGroup, Radio, FormControlLabel, FormControl, FormLabel,
    Checkbox, FormGroup, IconButton, Divider, Alert, CircularProgress,
    Chip
} from '@mui/material';
import { Add, Delete, CloudUpload, CheckCircle, ArrowBack, ArrowForward } from '@mui/icons-material';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const storage = getStorage();

const API_BASE = "https://www.geniusmileniaeducation.link/db-api";

const INTAKE_YEAR = "2026";

const NRIC_REGEX = /^\d{6}-\d{2}-\d{4}$/;
const PHONE_REGEX = /^01\d{8,9}$/;

const isValidNric = (value) => {
    const match = /^(\d{2})(\d{2})(\d{2})-\d{2}-\d{4}$/.exec(value.trim());
    if (!match) return false;
    const [, yy, mm, dd] = match;
    const month = Number(mm);
    const day = Number(dd);
    if (month < 1 || month > 12) return false;
    const currentYY = new Date().getFullYear() % 100;
    const fullYear = Number(yy) <= currentYY ? 2000 + Number(yy) : 1900 + Number(yy);
    const daysInMonth = new Date(fullYear, month, 0).getDate();
    return day >= 1 && day <= daysInMonth;
};

// --- Design tokens (matches reference UI) ---
const colors = {
    accent: '#FF8A00',
    accentSoft: '#FFF3E2',
    fieldBg: '#F1F2F6',
    text: '#232529',
    textMuted: '#8A8F98',
    cardBorder: '#EEF0F3'
};

// --- Services & branches ---
const services = [
    {
        id: 'ptgm',
        name: 'Pusat Tuisyen Genius Milenia',
        branches: ['PJCC', 'Bangsar South', 'Equine Park'],
        packages: [
            'PAKEJ UASA SR',
            'PAKEJ UASA SM',
            'PAKEJ SPM (TERAS)',
            'PAKEJ SPM (ALIRAN SAINS)',
            'COMBO UASA + UPKK',
            'KELAS PERSENDIRIAN'
        ]
    },
    {
        id: 'adeen',
        name: 'Akademi Adeen',
        branches: ['PJCC'],
        packages: [
            'PAKEJ UPKK',
            'PAKEJ MENGAJI',
            'PAKEJ FARDU AIN',
            'COMBO MENGAJI + FARDU AIN',
            'KELAS PERSENDIRIAN'
        ]
    },
    {
        id: 'qalistar',
        name: 'Tadika Genius Qalistar',
        branches: ['Petaling Jaya'],
        packages: ['JUNIOR STAR', 'ALL STAR', 'LEADER STAR']
    }
];

const classDayOptions = ["ISNIN", "SELASA", "RABU", "KHAMIS", "JUMAAT", "SABTU"];

const termsList = [
    "Segala yuran perlu dijelaskan sebelum atau pada 7 haribulan setiap bulan.",
    "Pelajar tidak dibenarkan memasuki kelas sekiranya yuran semasa dijelaskan selepas 7 haribulan pada bulan tersebut.",
    "Segala yuran atau bayaran tidak akan dikembalikan.",
    "Tiada kelas ganti yang akan diadakan untuk sesi kelas cuti umum.",
    "Pelajar mestilah berusaha bersungguh-sungguh untuk meningkatkan prestasi akademik sepanjang berada di PTGM.",
    "Pembayaran lewat yang dibuat selepas 7 haribulan akan dikenakan penalti bayaran RM 10.00."
];

const makeEmptyStudent = () => ({
    localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: '', age: '', icNumber: '', phone: '', address: '', sameAddressAsFirst: false,
    isHealthy: 'yes', healthDetails: '',
    orphanCertUrls: [], uploadingOrphanCert: false,
    package: '', electiveSubjects: '', classDays: []
});

const emptyParent = () => ({ name: '', icNumber: '', occupation: '', phone: '' });

const steps = ["Perkhidmatan", "Ibu Bapa/Penjaga", "Pelajar", "Syarat & Bayaran"];

// --- Shared styling helpers (mirrors the reference sign-up UI) ---
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: colors.fieldBg,
        borderRadius: '10px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1.5px solid ${colors.accent}`,
    },
    '& .MuiInputBase-input::placeholder': {
        color: colors.textMuted,
        opacity: 1,
    },
};

const radioSx = {
    color: '#D0D3D9',
    '&.Mui-checked': { color: colors.accent },
};

const checkboxSx = {
    color: '#D0D3D9',
    '&.Mui-checked': { color: colors.accent },
};

const pillButtonSx = {
    borderRadius: '999px',
    textTransform: 'none',
    fontWeight: 700,
    px: 3,
};

// Label rendered above a field, like "Full Name" in the reference UI
const FieldLabel = ({ children, required }) => (
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.text, mb: 0.75 }}>
        {children}{required ? ' *' : ''}
    </Typography>
);

// Wraps a label + input together
const LabeledField = ({ label, required, children }) => (
    <Box>
        <FieldLabel required={required}>{label}</FieldLabel>
        {children}
    </Box>
);

// Step indicator with numbered circles + connecting line (matches reference UI)
const StepIndicator = ({ currentStep }) => (
    <Stack direction="row" alignItems="flex-start" justifyContent="center" sx={{ mb: 4 }}>
        {steps.map((label, i) => (
            <React.Fragment key={label}>
                <Box sx={{ textAlign: 'center', minWidth: 64 }}>
                    <Box sx={{
                        width: 30, height: 30, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: i <= currentStep ? colors.accent : '#fff',
                        border: i <= currentStep ? 'none' : '2px solid #E3E5E9',
                        color: i <= currentStep ? '#fff' : '#B0B4BA',
                        fontWeight: 700, fontSize: 13, mx: 'auto'
                    }}>
                        {i + 1}
                    </Box>
                    <Typography sx={{
                        fontSize: 11, fontWeight: 600, mt: 0.75,
                        color: i === currentStep ? colors.text : colors.textMuted
                    }}>
                        {label}
                    </Typography>
                </Box>
                {i < steps.length - 1 && (
                    <Box sx={{
                        flex: 1, height: 2, mt: 1.85, mx: 0.5,
                        bgcolor: i < currentStep ? colors.accent : '#E3E5E9'
                    }} />
                )}
            </React.Fragment>
        ))}
    </Stack>
);

const PageBackground = ({ children }) => (
    <Box sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #FCEAD2 0%, #FCEAD2 42%, #FF9416 42%, #FF9416 100%)',
        py: { xs: 4, sm: 6 },
        px: 2,
    }}>
        <Box sx={{ position: 'absolute', top: -70, left: '28%', width: 260, height: 260, borderRadius: '50%', bgcolor: '#FFDBA6', opacity: 0.6 }} />
        <Box sx={{ position: 'absolute', top: 40, left: -80, width: 180, height: 180, borderRadius: '50%', bgcolor: '#FFFFFF', opacity: 0.25 }} />
        <Box sx={{ position: 'absolute', top: 120, right: -60, width: 200, height: 200, borderRadius: '50%', bgcolor: '#FFFFFF', opacity: 0.25 }} />
        <Box sx={{ position: 'absolute', bottom: -60, left: 30, width: 220, height: 220, borderRadius: '50%', bgcolor: '#FFA733', opacity: 0.5 }} />
        <Box sx={{ position: 'absolute', bottom: -40, right: -50, width: 240, height: 240, borderRadius: '50%', bgcolor: '#FFA733', opacity: 0.5 }} />
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
            {children}
        </Container>
    </Box>
);

const PublicRegistrationScreen = () => {
    const [sessionId] = useState(() => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

    const [step, setStep] = useState(0);
    const [formError, setFormError] = useState('');

    const [selectedServiceId, setSelectedServiceId] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');

    const [father, setFather] = useState(emptyParent());
    const [mother, setMother] = useState(emptyParent());

    const [students, setStudents] = useState([makeEmptyStudent()]);

    const [agreedTerms, setAgreedTerms] = useState(termsList.map(() => false));
    const [paymentProofUrl, setPaymentProofUrl] = useState('');
    const [paymentProofName, setPaymentProofName] = useState('');
    const [uploadingPayment, setUploadingPayment] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const selectedService = services.find((s) => s.id === selectedServiceId) || null;
    const selectedPackages = selectedService?.packages || [];
    const showsElectiveSubjects = selectedServiceId === 'ptgm' || selectedServiceId === 'adeen';
    const showsClassDays = selectedServiceId === 'ptgm' || selectedServiceId === 'adeen';

    // --- Service/branch helpers ---
    const handleSelectService = (id) => {
        if (id !== selectedServiceId) {
            setStudents((prev) => prev.map((student) => ({
                ...student,
                package: '',
                electiveSubjects: id === 'ptgm' ? student.electiveSubjects : '',
                classDays: id === 'ptgm' || id === 'adeen' ? student.classDays : []
            })));
        }
        setSelectedServiceId(id);
        const svc = services.find((s) => s.id === id);
        // Only one branch available -> auto-select it. Multiple branches -> user must choose.
        setSelectedBranch(svc.branches.length === 1 ? svc.branches[0] : '');
    };

    // --- Firebase Storage upload helper ---
    const uploadFile = async (file, path) => {
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, file);
        return await getDownloadURL(fileRef);
    };

    // --- Student helpers ---
    const updateStudent = (localId, field, value) => {
        setStudents((prev) => {
            const updated = prev.map((s) => s.localId === localId ? { ...s, [field]: value } : s);
            // If the first student's address changes, keep any "Sama seperti Pelajar 1" students in sync
            if (field === 'address' && prev[0]?.localId === localId) {
                return updated.map((s, idx) => (idx > 0 && s.sameAddressAsFirst) ? { ...s, address: value } : s);
            }
            return updated;
        });
    };

    const toggleSameAddressAsFirst = (localId) => {
        setStudents((prev) => {
            const firstAddress = prev[0]?.address || '';
            return prev.map((s) => {
                if (s.localId !== localId) return s;
                const next = !s.sameAddressAsFirst;
                return { ...s, sameAddressAsFirst: next, address: next ? firstAddress : s.address };
            });
        });
    };

    const toggleClassDay = (localId, day) => {
        setStudents((prev) => prev.map((s) => {
            if (s.localId !== localId) return s;
            const has = s.classDays.includes(day);
            return { ...s, classDays: has ? s.classDays.filter(d => d !== day) : [...s.classDays, day] };
        }));
    };

    const addStudent = () => setStudents((prev) => [...prev, makeEmptyStudent()]);
    const removeStudent = (localId) => setStudents((prev) => prev.length > 1 ? prev.filter(s => s.localId !== localId) : prev);

    const handleOrphanCertUpload = async (localId, fileList) => {
        const files = Array.from(fileList).slice(0, 5);
        if (files.length === 0) return;
        updateStudent(localId, 'uploadingOrphanCert', true);
        try {
            const urls = [];
            for (let i = 0; i < files.length; i++) {
                const url = await uploadFile(files[i], `registrations/${sessionId}/${localId}/orphan_cert_${Date.now()}_${files[i].name}`);
                urls.push(url);
            }
            setStudents((prev) => prev.map((s) => s.localId === localId
                ? { ...s, orphanCertUrls: [...s.orphanCertUrls, ...urls], uploadingOrphanCert: false }
                : s));
        } catch (error) {
            setFormError("Gagal memuat naik fail. Sila cuba lagi.");
            updateStudent(localId, 'uploadingOrphanCert', false);
        }
    };

    const handlePaymentUpload = async (file) => {
        if (!file) return;
        setUploadingPayment(true);
        try {
            const url = await uploadFile(file, `registrations/${sessionId}/payment_proof_${Date.now()}_${file.name}`);
            setPaymentProofUrl(url);
            setPaymentProofName(file.name);
        } catch (error) {
            setFormError("Gagal memuat naik bukti pembayaran. Sila cuba lagi.");
        } finally {
            setUploadingPayment(false);
        }
    };

    const toggleTerm = (index) => {
        setAgreedTerms((prev) => prev.map((v, i) => i === index ? !v : v));
    };

    // --- Validation ---
    const validateStep = (targetStep) => {
        if (targetStep === 0) {
            if (!selectedServiceId) {
                return "Sila pilih perkhidmatan.";
            }
            const svc = services.find((s) => s.id === selectedServiceId);
            if (svc.branches.length > 1 && !selectedBranch) {
                return "Sila pilih cawangan.";
            }
        }
        if (targetStep === 1) {
            // Only one guardian is required, but whichever is filled in must be complete and valid.
            // Both cannot be left entirely empty.
            const guardianStatus = (p) => {
                const filledCount = [p.name, p.icNumber, p.occupation, p.phone].filter((v) => v.trim()).length;
                if (filledCount === 0) return 'empty';
                if (filledCount === 4) return 'complete';
                return 'partial';
            };
            const fatherStatus = guardianStatus(father);
            const motherStatus = guardianStatus(mother);

            if (fatherStatus === 'empty' && motherStatus === 'empty') {
                return "Sila isi maklumat sekurang-kurangnya seorang penjaga (Ayah/Penjaga atau Ibu/Penjaga).";
            }
            if (fatherStatus === 'partial' || motherStatus === 'partial') {
                return "Sila lengkapkan semua maklumat bagi penjaga yang telah diisi.";
            }
            for (const [label, p, status] of [
                ['Ayah/Penjaga', father, fatherStatus],
                ['Ibu/Penjaga', mother, motherStatus],
            ]) {
                if (status !== 'complete') continue;
                if (!NRIC_REGEX.test(p.icNumber.trim()) || !isValidNric(p.icNumber)) {
                    return `No Kad Pengenalan ${label} tidak sah. Format: xxxxxx-xx-xxxx.`;
                }
                if (!PHONE_REGEX.test(p.phone.trim())) {
                    return `No Telefon ${label} tidak sah. Mesti bermula dengan 01 dan panjang 10-11 digit dan tanpa simbol (-).`;
                }
            }
        }
        if (targetStep === 2) {
            for (const s of students) {
                if (!s.name.trim() || !s.icNumber.trim() || !s.address.trim()) {
                    return "Sila lengkapkan semua maklumat wajib bagi setiap pelajar.";
                }
                const ageNum = Number(s.age);
                if (Number.isNaN(ageNum) || !Number.isInteger(ageNum) || ageNum < 1) {
                    return "Umur pelajar tidak sah.";
                }
                if (!NRIC_REGEX.test(s.icNumber.trim()) || !isValidNric(s.icNumber)) {
                    return "No Kad Pengenalan pelajar tidak sah. Format: xxxxxx-xx-xxxx.";
                }
                // No Telefon Pelajar is optional — only validate the format if the user filled it in.
                if (s.phone.trim() && !PHONE_REGEX.test(s.phone.trim())) {
                    return "No Telefon pelajar tidak sah. Mesti bermula dengan 01 dan panjang 10-11 digit dan tanpa simbol (-).";
                }
                if (s.isHealthy === 'no' && !s.healthDetails.trim()) {
                    return "Sila nyatakan penyakit/alahan/keperluan khas pelajar.";
                }
                if (!s.package) {
                    return "Sila pilih pakej bagi setiap pelajar.";
                }
                if (showsClassDays && s.classDays.length === 0) {
                    return "Sila pilih sekurang-kurangnya satu hari kelas bagi setiap pelajar.";
                }
            }
        }
        if (targetStep === 3) {
            if (agreedTerms.some((v) => !v)) {
                return "Sila bersetuju dengan semua syarat dan peraturan.";
            }
            if (!paymentProofUrl) {
                return "Sila muat naik bukti pembayaran.";
            }
        }
        return '';
    };

    const goNext = () => {
        const err = validateStep(step);
        if (err) {
            setFormError(err);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setFormError('');
        setStep((s) => Math.min(s + 1, steps.length - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goBack = () => {
        setFormError('');
        setStep((s) => Math.max(s - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async () => {
        const err = validateStep(3);
        if (err) {
            setFormError(err);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setFormError('');
        setSubmitting(true);
        try {
            const payload = {
                service: selectedService?.name,
                branch: selectedBranch,
                father, mother,
                students: students.map(({ uploadingOrphanCert, localId, ...rest }) => rest),
                paymentProofUrl,
                intakeYear: INTAKE_YEAR
            };
            const res = await fetch(`${API_BASE}/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const json = await res.json();
            if (json.status === 1) {
                setSubmitted(true);
            } else {
                setFormError(json.message || "Penghantaran gagal. Sila cuba lagi.");
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            setFormError("Penghantaran gagal. Sila semak sambungan internet anda dan cuba lagi.");
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setSelectedServiceId('');
        setSelectedBranch('');
        setFather(emptyParent());
        setMother(emptyParent());
        setStudents([makeEmptyStudent()]);
        setAgreedTerms(termsList.map(() => false));
        setPaymentProofUrl('');
        setPaymentProofName('');
        setStep(0);
        setSubmitted(false);
    };

    // --- Success screen ---
    if (submitted) {
        return (
            <PageBackground>
                <Paper sx={{ p: 4, borderRadius: 5, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
                    <CheckCircle sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Pendaftaran Berjaya!</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Terima kasih. Borang pendaftaran anda telah diterima dan akan disemak oleh pihak{' '}
                        {selectedService?.name} {selectedBranch}.
                    </Typography>
                    <Button variant="contained" onClick={resetForm} sx={{ ...pillButtonSx, bgcolor: colors.accent }}>
                        Daftar Pelajar Lain
                    </Button>
                </Paper>
            </PageBackground>
        );
    }

    return (
        <PageBackground>
            <img src={require("../../images/GME-Logo.png")} alt="Genius Milenia Logo" style={{ width: "100%", height: "100px", objectFit: "contain", marginBottom: 30 }} />
            <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: 5, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
                <Typography variant="h5" fontWeight="bold" textAlign="center" sx={{ mb: 3 }}>
                    Borang Pendaftaran
                </Typography>

                <StepIndicator currentStep={step} />

                {selectedService && step > 0 && (
                    <Chip
                        label={`${selectedService.name}${selectedBranch ? ` • ${selectedBranch}` : ''}`}
                        sx={{ bgcolor: colors.accentSoft, color: colors.accent, fontWeight: 700, mb: 2 }}
                    />
                )}

                {formError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{formError}</Alert>}

                {/* STEP 0 — Service & Branch */}
                {step === 0 && (
                    <Stack spacing={3}>
                        <Box>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.text, mb: 1.5 }}>
                                Pilih Perkhidmatan *
                            </Typography>
                            <Stack spacing={1.5}>
                                {services.map((svc) => (
                                    <Paper
                                        key={svc.id}
                                        onClick={() => handleSelectService(svc.id)}
                                        elevation={0}
                                        sx={{
                                            p: 2, borderRadius: 3, cursor: 'pointer',
                                            border: selectedServiceId === svc.id ? `2px solid ${colors.accent}` : `1.5px solid ${colors.cardBorder}`,
                                            bgcolor: selectedServiceId === svc.id ? colors.accentSoft : '#fff',
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        <Radio checked={selectedServiceId === svc.id} size="small" sx={{ ...radioSx, p: 0 }} />
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: colors.text }}>
                                                {svc.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                                                Cawangan: {svc.branches.join(' / ')}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>

                        {selectedService && selectedService.branches.length > 1 && (
                            <Box>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: colors.text, mb: 1.5 }}>
                                    Pilih Cawangan *
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={1.25}>
                                    {selectedService.branches.map((branch) => (
                                        <Chip
                                            key={branch}
                                            label={branch}
                                            onClick={() => setSelectedBranch(branch)}
                                            sx={{
                                                fontWeight: 600, fontSize: 13, px: 1, py: 2.5, borderRadius: '999px',
                                                bgcolor: selectedBranch === branch ? colors.accent : colors.fieldBg,
                                                color: selectedBranch === branch ? '#fff' : colors.text,
                                                '&:hover': { bgcolor: selectedBranch === branch ? colors.accent : '#E7E9EE' },
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {selectedService && selectedService.branches.length === 1 && (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Cawangan dipilih secara automatik: <strong>{selectedService.branches[0]}</strong>
                            </Alert>
                        )}
                    </Stack>
                )}

                {/* STEP 1 — Parent Info */}
                {step === 1 && (
                    <Stack spacing={2}>
                        {[{ label: 'AYAH/PENJAGA', state: father, setState: setFather },
                          { label: 'IBU/PENJAGA', state: mother, setState: setMother }].map(({ label, state, setState }) => (
                            <Paper key={label} elevation={0} sx={{ p: 3, borderRadius: 4, border: `1px solid ${colors.cardBorder}` }}>
                                <Typography variant="subtitle1" fontWeight="bold" color={colors.accent} sx={{ mb: 2 }}>
                                    NAMA {label}
                                </Typography>
                                <Stack spacing={2}>
                                    <LabeledField label={`Nama ${label}`} required>
                                        <TextField required fullWidth size="small" placeholder="Enter Full Name" sx={fieldSx}
                                            value={state.name} onChange={(e) => setState({ ...state, name: e.target.value })} />
                                    </LabeledField>
                                    <LabeledField label="No Kad Pengenalan" required>
                                        <TextField required fullWidth size="small" placeholder="xxxxxx-xx-xxxx" sx={fieldSx}
                                            value={state.icNumber} onChange={(e) => setState({ ...state, icNumber: e.target.value })} />
                                    </LabeledField>
                                    <LabeledField label="Perkerjaan" required>
                                        <TextField required fullWidth size="small" placeholder="Enter Occupation" sx={fieldSx}
                                            value={state.occupation} onChange={(e) => setState({ ...state, occupation: e.target.value })} />
                                    </LabeledField>
                                    <LabeledField label={`No Telefon ${label}`} required>
                                        <TextField required fullWidth size="small" placeholder="01xxxxxxxx" sx={fieldSx}
                                            value={state.phone} onChange={(e) => setState({ ...state, phone: e.target.value })} />
                                    </LabeledField>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                )}

                {/* STEP 2 — Students */}
                {step === 2 && (
                    <Stack spacing={2}>
                        {students.map((s, index) => (
                            <Paper key={s.localId} elevation={0} sx={{ p: 3, borderRadius: 4, border: `1px solid ${colors.cardBorder}` }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                    <Chip label={`PELAJAR ${index + 1}`} sx={{ bgcolor: colors.accent, color: '#fff', fontWeight: 'bold' }} />
                                    {students.length > 1 && (
                                        <IconButton size="small" color="error" onClick={() => removeStudent(s.localId)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    )}
                                </Stack>

                                <Stack spacing={2}>
                                    <LabeledField label="Nama Pelajar" required>
                                        <TextField required fullWidth size="small" placeholder="Enter Student's Full Name" sx={fieldSx}
                                            value={s.name} onChange={(e) => updateStudent(s.localId, 'name', e.target.value)} />
                                    </LabeledField>
                                    <LabeledField label="Umur Pelajar" required>
                                        <TextField required fullWidth size="small" type="number" placeholder="Enter Age" sx={fieldSx}
                                            inputProps={{ min: 1, step: 1 }}
                                            value={s.age}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                // Allow clearing the field, otherwise keep it a positive whole number (>= 1)
                                                if (raw === '') { updateStudent(s.localId, 'age', ''); return; }
                                                const n = Math.floor(Number(raw));
                                                if (Number.isNaN(n)) return;
                                                updateStudent(s.localId, 'age', String(Math.max(1, n)));
                                            }} />
                                    </LabeledField>
                                    <LabeledField label="No Kad Pengenalan Pelajar" required>
                                        <TextField required fullWidth size="small" placeholder="xxxxxx-xx-xxxx" sx={fieldSx}
                                            value={s.icNumber} onChange={(e) => updateStudent(s.localId, 'icNumber', e.target.value)} />
                                    </LabeledField>
                                    <LabeledField label="No Telefon Pelajar">
                                        <TextField fullWidth size="small" placeholder="01xxxxxxxx (pilihan)" sx={fieldSx}
                                            value={s.phone} onChange={(e) => updateStudent(s.localId, 'phone', e.target.value)} />
                                    </LabeledField>
                                    <LabeledField label="Alamat Tempat Tinggal" required>
                                        {index > 0 && (
                                            <FormControlLabel
                                                sx={{ mb: 0.5 }}
                                                control={
                                                    <Checkbox size="small" checked={s.sameAddressAsFirst} sx={checkboxSx}
                                                        onChange={() => toggleSameAddressAsFirst(s.localId)} />
                                                }
                                                label={<Typography variant="caption">Sama seperti alamat Pelajar 1</Typography>}
                                            />
                                        )}
                                        <TextField required fullWidth size="small" multiline rows={2} placeholder="Enter Address" sx={fieldSx}
                                            value={s.address}
                                            disabled={index > 0 && s.sameAddressAsFirst}
                                            onChange={(e) => updateStudent(s.localId, 'address', e.target.value)} />
                                    </LabeledField>

                                    <Divider sx={{ borderColor: colors.cardBorder }} />

                                    <FormControl>
                                        <FormLabel sx={{ fontWeight: 700, fontSize: 13, color: colors.text }}>
                                            DEKLARASI KESIHATAN PELAJAR *
                                        </FormLabel>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                                            Saya mengesahkan bahawa maklumat kesihatan pelajar yang didaftarkan adalah benar.
                                        </Typography>
                                        <RadioGroup
                                            value={s.isHealthy}
                                            onChange={(e) => updateStudent(s.localId, 'isHealthy', e.target.value)}
                                        >
                                            <FormControlLabel value="yes" control={<Radio size="small" sx={radioSx} />}
                                                label="Pelajar sihat dan tiada penyakit/keperluan khas" />
                                            <FormControlLabel value="no" control={<Radio size="small" sx={radioSx} />}
                                                label="Pelajar mempunyai penyakit/alahan/keperluan khas" />
                                        </RadioGroup>
                                        {s.isHealthy === 'no' && (
                                            <TextField
                                                placeholder="Sila nyatakan (cth: asma, epilepsi, diabetes, autisme, ADHD, alahan makanan/ubatan)"
                                                required fullWidth size="small" multiline rows={2} sx={{ mt: 1, ...fieldSx }}
                                                value={s.healthDetails}
                                                onChange={(e) => updateStudent(s.localId, 'healthDetails', e.target.value)}
                                            />
                                        )}
                                    </FormControl>

                                    <Divider sx={{ borderColor: colors.cardBorder }} />

                                    <Box>
                                        <Typography variant="caption" fontWeight="bold" color={colors.text}>
                                            SEKIRANYA ANAK YATIM, SILA MUAT NAIK SIJIL KEMATIAN (PILIHAN)
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            <Button
                                                component="label"
                                                variant="outlined"
                                                size="small"
                                                startIcon={s.uploadingOrphanCert ? <CircularProgress size={16} /> : <CloudUpload />}
                                                disabled={s.uploadingOrphanCert}
                                                sx={{ borderRadius: 2, textTransform: 'none' }}
                                            >
                                                {s.uploadingOrphanCert ? "Uploading..." : "Add File"}
                                                <input type="file" hidden multiple accept="image/*,application/pdf"
                                                    onChange={(e) => handleOrphanCertUpload(s.localId, e.target.files)} />
                                            </Button>
                                            {s.orphanCertUrls.length > 0 && (
                                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="text.secondary">
                                                    {s.orphanCertUrls.length} file(s) uploaded
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    <Divider sx={{ borderColor: colors.cardBorder }} />

                                    <FormControl>
                                        <FormLabel sx={{ fontWeight: 700, fontSize: 13, color: colors.text }}>PAKEJ PILIHAN *</FormLabel>
                                        <RadioGroup
                                            value={s.package}
                                            onChange={(e) => updateStudent(s.localId, 'package', e.target.value)}
                                        >
                                            {selectedPackages.map((p) => (
                                                <FormControlLabel key={p} value={p} control={<Radio size="small" sx={radioSx} />} label={p} />
                                            ))}
                                        </RadioGroup>
                                    </FormControl>

                                    {showsElectiveSubjects && (
                                        <LabeledField label="Subjek Elektif (1 subjek atau 3 subjek), sila nyatakan">
                                            <TextField fullWidth size="small" placeholder="Type Here..." sx={fieldSx}
                                                value={s.electiveSubjects}
                                                onChange={(e) => updateStudent(s.localId, 'electiveSubjects', e.target.value)} />
                                        </LabeledField>
                                    )}

                                    {showsClassDays && (
                                        <FormControl>
                                            <FormLabel sx={{ fontWeight: 700, fontSize: 13, color: colors.text }}>
                                                WAKTU KELAS (ISNIN-JUMAAT MALAM, SABTU PAGI) *
                                            </FormLabel>
                                            <FormGroup row>
                                                {classDayOptions.map((day) => (
                                                    <FormControlLabel
                                                        key={day}
                                                        control={
                                                            <Checkbox size="small" checked={s.classDays.includes(day)} sx={checkboxSx}
                                                                onChange={() => toggleClassDay(s.localId, day)} />
                                                        }
                                                        label={day}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </FormControl>
                                    )}
                                </Stack>
                            </Paper>
                        ))}

                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={addStudent}
                            sx={{ borderRadius: 3, borderStyle: 'dashed', py: 1.5, textTransform: 'none', fontWeight: 700, color: colors.accent, borderColor: colors.accent }}
                        >
                            Tambah Pelajar Lain
                        </Button>
                    </Stack>
                )}

                {/* STEP 3 — Terms & Payment */}
                {step === 3 && (
                    <Stack spacing={2}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: `1px solid ${colors.cardBorder}` }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                SYARAT-SYARAT DAN PERATURAN *
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Perlu dibaca dan difahami oleh ibu bapa dan pelajar. Sila tandakan setuju dengan setiap syarat di bawah.
                            </Typography>
                            <FormGroup>
                                {termsList.map((term, i) => (
                                    <FormControlLabel
                                        key={i}
                                        control={<Checkbox size="small" checked={agreedTerms[i]} onChange={() => toggleTerm(i)} sx={checkboxSx} />}
                                        label={<Typography variant="body2">{term}</Typography>}
                                        sx={{ alignItems: 'flex-start', mt: 0.5 }}
                                    />
                                ))}
                            </FormGroup>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: `1px solid ${colors.cardBorder}` }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                SILA MUAT NAIK BUKTI PEMBAYARAN DI SINI *
                            </Typography>
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={uploadingPayment ? <CircularProgress size={16} /> : <CloudUpload />}
                                disabled={uploadingPayment}
                                sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                                {uploadingPayment ? "Uploading..." : (paymentProofName || "Add File")}
                                <input type="file" hidden accept="image/*,application/pdf"
                                    onChange={(e) => handlePaymentUpload(e.target.files[0])} />
                            </Button>
                            {paymentProofUrl && (
                                <Typography variant="caption" display="block" sx={{ mt: 1 }} color="success.main">
                                    ✓ Bukti pembayaran berjaya dimuat naik
                                </Typography>
                            )}
                        </Paper>
                    </Stack>
                )}

                {/* Navigation */}
                <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mt: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={goBack}
                        disabled={step === 0 || submitting}
                        sx={{ ...pillButtonSx }}
                        fullWidth={window.innerWidth < 600}
                    >
                        Kembali
                    </Button>
                    {step < steps.length - 1 ? (
                        <Button
                            variant="contained"
                            endIcon={<ArrowForward />}
                            onClick={goNext}
                            sx={{ ...pillButtonSx, bgcolor: colors.accent, '&:hover': { bgcolor: '#E67D00' } }}
                            fullWidth={window.innerWidth < 600}
                        >
                            Seterusnya
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ ...pillButtonSx, bgcolor: colors.accent, '&:hover': { bgcolor: '#E67D00' } }}
                            fullWidth={window.innerWidth < 600}
                        >
                            {submitting ? "Menghantar..." : "Submit"}
                        </Button>
                    )}
                </Stack>
            </Paper>
        </PageBackground>
    );
};

export default PublicRegistrationScreen;